import { booleanAttribute, Directive, input, output, signal } from '@angular/core';

type DroppedFile = { entry: FileSystemFileEntry; path: string };

@Directive({
  selector: '[file-drop]',
  host: {
    '[class.file-drop--active]': 'isDragging()',
    '(dragenter)': 'onDragEnter($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
  },
})
export class FileDropDirective {
  readonly enabled = input(true, { alias: 'file-drop', transform: booleanAttribute });
  readonly filesUpload = output<{ files: File[]; paths: string[] }>();

  protected readonly isDragging = signal(false);
  #dragEnterCount = 0;

  onDragEnter(event: DragEvent): void {
    if (!this.isFileDrag(event)) return;

    this.preventDefault(event);
    this.#dragEnterCount++;
    this.isDragging.set(true);
  }

  onDragOver(event: DragEvent): void {
    if (!this.isFileDrag(event)) return;

    this.preventDefault(event);
    event.dataTransfer!.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent): void {
    if (!this.isFileDrag(event)) return;

    this.preventDefault(event);
    this.#dragEnterCount = Math.max(0, this.#dragEnterCount - 1);

    if (this.#dragEnterCount === 0) {
      this.isDragging.set(false);
    }
  }

  onDrop(event: DragEvent): void {
    this.preventDefault(event);
    this.#dragEnterCount = 0;
    this.isDragging.set(false);

    const items = Array.from(event.dataTransfer?.items ?? [])
      .map((item) => item.webkitGetAsEntry())
      .filter((e): e is FileSystemEntry => !!e);

    void this.getFileTree(items).then((filesToUpload) => {
      if (filesToUpload.length) this.uploadFile(filesToUpload);
    });
  }

  private async getFileTree(items: FileSystemEntry[], path = ''): Promise<DroppedFile[]> {
    const files: DroppedFile[] = [];

    for (const item of items) {
      if (item.isFile) {
        files.push({ entry: item as FileSystemFileEntry, path });
      } else if (item.isDirectory) {
        const reader = (item as FileSystemDirectoryEntry).createReader();

        const readBatch = (): Promise<FileSystemEntry[]> =>
          new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        const childPath = path ? `${path}/${item.name}` : item.name;

        let batch = await readBatch();
        while (batch.length) {
          files.push(...(await this.getFileTree(batch, childPath)));
          batch = await readBatch();
        }
      }
    }

    return files;
  }

  private uploadFile(items: DroppedFile[]): void {
    const resolved = items.map(
      ({ entry }) => new Promise<File>((resolve, reject) => entry.file(resolve, reject)),
    );

    void Promise.all(resolved).then((files) => {
      this.filesUpload.emit({ files, paths: items.map(({ path }) => path) });
    });
  }

  private isFileDrag(event: DragEvent): boolean {
    return this.enabled() && event.dataTransfer?.types.includes('Files') === true;
  }

  private preventDefault(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }
}
